import { useState } from 'react';
import {
  X,
  AlertTriangle,
  Package,
  Image,
  Loader2,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCreateDiscrepancy } from '../../hooks/use-discrepancies';
import type {
  DiscrepancyType, Delivery, DeliveryLine 
} from '../../types';
import { DISCREPANCY_TYPE_LABELS } from '../../types/discrepancy';

interface ReportDiscrepancyModalProps {
  readonly delivery: Delivery;
  readonly onClose: () => void;
}

const DISCREPANCY_TYPES: {
  type: DiscrepancyType;
  description: string 
}[] = [
  {
    type: 'missing_items',
    description: 'Items not included in delivery' 
  },
  {
    type: 'damaged_items',
    description: 'Items arrived damaged or spoiled' 
  },
  {
    type: 'wrong_items',
    description: 'Received different items than ordered' 
  },
  {
    type: 'quantity_mismatch',
    description: 'Quantity differs from order' 
  },
  {
    type: 'quality_issue',
    description: 'Quality below expected standard' 
  },
];

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

export function ReportDiscrepancyModal({
  delivery, onClose 
}: ReportDiscrepancyModalProps) {
  const [step, setStep] = useState<'type' | 'items' | 'details'>('type');
  const [selectedType, setSelectedType] = useState<DiscrepancyType | null>(null);
  const [selectedItems, setSelectedItems] = useState<Map<string, number>>(new Map());
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]);

  const createDiscrepancy = useCreateDiscrepancy();

  const handleTypeSelect = (type: DiscrepancyType) => {
    setSelectedType(type);
    setStep('items');
  };

  const toggleItem = (line: DeliveryLine) => {
    const newSelected = new Map(selectedItems);
    if (newSelected.has(line.lineId)) {
      newSelected.delete(line.lineId);
    } else {
      newSelected.set(line.lineId, 1);
    }
    setSelectedItems(newSelected);
  };

  const updateQuantity = (lineId: string, quantity: number) => {
    const newSelected = new Map(selectedItems);
    if (quantity <= 0) {
      newSelected.delete(lineId);
    } else {
      newSelected.set(lineId, quantity);
    }
    setSelectedItems(newSelected);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    setImages(prev => [...prev, ...imageFiles].slice(0, 5));
  };

  const removeImage = (fileToRemove: File) => {
    setImages(prev => prev.filter(f => f !== fileToRemove));
  };

  const handleSubmit = () => {
    if (!selectedType || selectedItems.size === 0) return;

    createDiscrepancy.mutate({
      poId: delivery.poId,
      deliveryId: delivery.deliveryId,
      type: selectedType,
      description,
      items: Array.from(selectedItems.entries()).map(([lineId, qty]) => ({
        lineId,
        affectedQuantity: qty,
      })),
    }, {onSuccess: () => onClose(),});
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-start justify-between border-b border-slate-100">
          <div>
            <CardTitle className="text-lg font-medium text-slate-900">
              Report Discrepancy
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              {delivery.poNumber} • {delivery.supplierName}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-4">
          {step === 'type' && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700 mb-4">
                What type of issue did you encounter?
              </p>
              {DISCREPANCY_TYPES.map((item) => (
                <button
                  key={item.type}
                  type="button"
                  onClick={() => handleTypeSelect(item.type)}
                  className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  <p className="font-medium text-slate-900">
                    {DISCREPANCY_TYPE_LABELS[item.type]}
                  </p>
                  <p className="text-sm text-slate-500 mt-0.5">{item.description}</p>
                </button>
              ))}
            </div>
          )}

          {step === 'items' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">
                  Select affected items
                </p>
                <Button variant="ghost" size="sm" onClick={() => setStep('type')}>
                  ← Back
                </Button>
              </div>

              <div className="space-y-2">
                {delivery.lines.map((line) => {
                  const isSelected = selectedItems.has(line.lineId);
                  const quantity = selectedItems.get(line.lineId) ?? 0;

                  return (
                    <div
                      key={line.lineId}
                      className={`p-3 rounded-lg border transition-colors ${
                        isSelected
                          ? 'border-slate-900 bg-slate-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <button
                          type="button"
                          onClick={() => toggleItem(line)}
                          className="flex-1 text-left"
                        >
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-slate-400" />
                            <span className="font-medium text-slate-900">{line.itemName}</span>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">
                            {line.sku} • Ordered: {line.orderedQuantity} {line.unit}
                          </p>
                        </button>
                        {isSelected && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-500">Affected:</span>
                            <input
                              type="number"
                              value={quantity}
                              onChange={(e) => updateQuantity(line.lineId, parseInt(e.target.value) || 0)}
                              min="1"
                              max={line.orderedQuantity}
                              className="w-20 rounded-md border border-slate-200 px-2 py-1 text-sm"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm text-slate-500">{line.unit}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedItems.size > 0 && (
                <Button
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  onClick={() => setStep('details')}
                >
                  Continue ({selectedItems.size} {pluralize(selectedItems.size, 'item', 'items')} selected)
                </Button>
              )}
            </div>
          )}

          {step === 'details' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-700">
                  Add details and evidence
                </p>
                <Button variant="ghost" size="sm" onClick={() => setStep('items')}>
                  ← Back
                </Button>
              </div>

              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-slate-900">
                    {selectedType && DISCREPANCY_TYPE_LABELS[selectedType]}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  {selectedItems.size} {pluralize(selectedItems.size, 'item', 'items')} affected
                </p>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the issue in detail..."
                  rows={4}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Evidence Photos (optional)
                </label>
                <div className="flex flex-wrap gap-2">
                  {images.map((file) => (
                    <div key={`image-${file.name}-${file.size}`} className="relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="h-20 w-20 object-cover rounded-md border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(file)}
                        className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <label className="h-20 w-20 rounded-md border-2 border-dashed border-slate-200 flex flex-col items-center justify-center cursor-pointer hover:border-slate-300 transition-colors">
                      <Image className="h-5 w-5 text-slate-400" />
                      <span className="text-xs text-slate-400 mt-1">Add</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  onClick={handleSubmit}
                  disabled={!description.trim() || createDiscrepancy.isPending}
                >
                  {createDiscrepancy.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Report'
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
